import { GAEventName } from 'analytics/ga-event-name.enum';
import { createAnalyticsEvent } from 'analytics/create-analytics-event';
import { StoreCode } from 'schema/storeCode';
import { ProductCartEventEnum } from './subscription';

import { CheckoutType } from './ecommerce';

export type CheckoutProductsType = {
  brand: string;
  id: number;
  name: string;
  price: string | number;
  productId: number;
  quantity: number;
  variant: string;
  variantId: number;
};

export const checkoutEvent = createAnalyticsEvent<{
  currencyCode: string | undefined;
  eventId: string;
  checkoutProducts: CheckoutProductsType[] | undefined;
}>(({ currencyCode, checkoutProducts, eventId }) => ({
  event: GAEventName.CHECKOUT,
  event_id: eventId,
  ecommerce: {
    currencyCode,
    checkout: {
      actionField: { step: 1, option: 'Initiate' },
      products: checkoutProducts
    }
  }
}));

export const expressCheckout = createAnalyticsEvent<{
  paymentType: string | null;
  pagePath: string;
}>(({ paymentType, pagePath }) => ({
  event: GAEventName.EXPRESS_CHECKOUT_CUSTOM_EVENT,
  eventCategory: 'Button Tracking',
  eventAction: `${paymentType} Express`,
  eventLabel: pagePath
}));

export const expressCheckoutAttempt = createAnalyticsEvent<{
  paymentType: string | null;
}>(({ paymentType }) => ({
  event: GAEventName.EXPRESS_CHECKOUT_ATTEMPT,
  payment_type: paymentType
}));

export const expressCheckoutFailure = createAnalyticsEvent<{
  paymentType: string | null;
  errorMessage: string;
}>(({ paymentType, errorMessage }) => ({
  event: GAEventName.EXPRESS_CHECKOUT_FAILURE,
  payment_type: paymentType,
  failure_reason: errorMessage
}));

export const applyDiscountSuccess = createAnalyticsEvent<{
  coupon: string;
  orderCouponAmount: number;
  checkoutType: CheckoutType;
}>(({ checkoutType, coupon, orderCouponAmount }) => ({
  event: GAEventName.APPLY_DISCOUNT_SUCCESS,
  checkout_type: checkoutType,
  coupon,
  order_coupon_amount: orderCouponAmount
}));

export const applyDiscountFailure = createAnalyticsEvent<{
  coupon: string;
  failureReason: string;
  checkoutType: CheckoutType;
}>(({ checkoutType, coupon, failureReason }) => ({
  event: GAEventName.APPLY_DISCOUNT_FAILURE,
  checkout_type: checkoutType,
  coupon,
  failure_reason: failureReason
}));

export const reviewOrder = createAnalyticsEvent<{
  store: StoreCode;
}>(({ store }) => ({
  event: GAEventName.REVIEW_ORDER,
  store
}));

export const changeShippingInfoEvent = createAnalyticsEvent(() => ({
  event: GAEventName.CHANGE_SHIPPING_INFO
}));

export const checkoutStepError = createAnalyticsEvent<{
  stepNumber: number;
  stepName: string;
  errorMessage: string;
  checkoutType?: CheckoutType;
}>(({ checkoutType = 'marketing', stepNumber, stepName, errorMessage }) => ({
  event: GAEventName.CHECKOUT_ERROR,
  checkout_type: checkoutType,
  checkout_step_number: stepNumber,
  checkout_step_name: stepName,
  failure_reason: errorMessage
}));

export const useSavedAddressCheckout = createAnalyticsEvent<{
  checkoutType: CheckoutType;
}>(({ checkoutType }) => ({
  event: GAEventName.USE_SAVED_ADDRESS_CHECKOUT,
  checkout_type: checkoutType
}));

export const deleteSavedAddressCheckout = createAnalyticsEvent<{
  checkoutType: CheckoutType;
}>(({ checkoutType }) => ({
  event: GAEventName.DELETE_SAVED_ADDRESS_CHECKOUT,
  checkout_type: checkoutType
}));

export const subcriptionShipmentFrequency = createAnalyticsEvent<{
  checkoutType?: CheckoutType;
  subscriptionId?: number;
  shipFrequency: string;
}>(({ checkoutType, subscriptionId, shipFrequency }) => ({
  event: GAEventName.SUBSCRIPTION_SHIPMENT_FREQUENCY,
  checkout_type: checkoutType,
  subscription_id: subscriptionId,
  ship_frequency: shipFrequency
}));

export const subscriptionPaymentInfo = createAnalyticsEvent<{
  checkoutType: CheckoutType;
  subscriptionId?: number;
  addToCartType?: ProductCartEventEnum;
  payMethod?: string;
}>(({ checkoutType, subscriptionId, addToCartType, payMethod }) => ({
  event: GAEventName.SUBSCRIPTION_PAYMENT_INFO,
  checkout_type: checkoutType,
  subscription_id: subscriptionId,
  add_to_cart_type: addToCartType,
  payment_type: payMethod
}));

export const checkoutCancel = createAnalyticsEvent<{
  checkoutType: CheckoutType;
  checkoutStepName: 'add shipping info' | 'add payment method' | 'change payment method';
}>(({ checkoutType, checkoutStepName }) => ({
  event: GAEventName.CHECKOUT_CANCEL,
  checkout_type: checkoutType,
  checkout_step_name: checkoutStepName
}));

export const editPaymentMethodCheckout = createAnalyticsEvent<{
  checkoutType: CheckoutType;
  paymentType?: string;
}>(({ checkoutType, paymentType }) => ({
  event: GAEventName.EDIT_PAYMENT_METHOD_CHECKOUT,
  checkout_type: checkoutType,
  payment_type: paymentType
}));

export const deleteSavedPaymentCheckout = createAnalyticsEvent<{
  checkoutType: CheckoutType;
  paymentType?: string;
}>(({ checkoutType, paymentType }) => ({
  event: GAEventName.DELETE_SAVED_PAYMENT_CHECKOUT,
  checkout_type: checkoutType,
  payment_type: paymentType
}));

export const orderDetailReview = createAnalyticsEvent<{
  checkoutType: CheckoutType;
}>(({ checkoutType }) => ({
  event: GAEventName.ORDER_DETAIL_REVIEW,
  checkout_type: checkoutType
}));
